import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { _, TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError, first, switchMap } from 'rxjs/operators';
import { ApiPath } from 'src/app/configs/api-paths';
import { SystemErrorResponse } from 'src/app/models/system-error';
import { RootStoreState } from 'src/app/root-store';
import { AuthStoreAction } from 'src/app/root-store/auth-store';
import { ErrorHandlingStoreAction } from 'src/app/root-store/error-handling-store';
import { PreferencesSelectors } from 'src/app/root-store/preferences-store';
import { TokenService } from 'src/app/services/auth/token.service';
import { MessageNotifierService } from 'src/app/services/utils/message-notifier.service';
import { environment } from 'src/environments/environment';
import { LogService } from '../../log-service';
import { ExcludedAPIServices } from './excluded-api-services';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  constructor(
    private messageNotifierService: MessageNotifierService,
    private store: Store<RootStoreState.State>,
    private translate: TranslateService,
    private tokenService: TokenService,
    private router: Router,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.store.select(PreferencesSelectors.selectPreferencesHelp).pipe(
      first(),
      switchMap((helpSetting) => {
        if (
          (!helpSetting || !request.url.match(helpSetting.settingHelpUrl)) &&
          !request.url.match(environment.getEndpoint(ApiPath.Public.IS_ALIVE))
        ) {
          return next.handle(request).pipe(
            // retry(1), /* Retry to call back-end service for n times*/
            catchError((response: HttpErrorResponse) => {
              return this.manageError(response, request);
            }),
          );
        } else {
          return next.handle(request);
        }
      }),
    );
  }

  private manageError(resp: HttpErrorResponse, request: HttpRequest<any>): Observable<HttpEvent<any>> {
    const m = this.manageError.name;

    // Use HttpErrorResponse.status as source of truth.
    // For proxy errors (e.g. 502), resp.error may be plain text/HTML and not carry a status field.
    const httpStatus = resp.status ?? resp.error?.status;

    if (httpStatus === 0 || httpStatus === 503 || httpStatus == null) {
      // UNDEFINED ERROR;
      LogService.error(this, m, resp.message, resp.error);
      if (request.url.includes(ApiPath.Notifications.NOTIFICATIONS_COUNT)) {
        // Browser can surface proxy/CORS failures as status 0 for polling calls.
        // Avoid switching the entire app to "server unreachable" mode for this endpoint.
        this.store.dispatch(ErrorHandlingStoreAction.setError());
      } else {
        this.store.dispatch(ErrorHandlingStoreAction.setServerUnreachableError());
      }
      return throwError(() => resp);
      // Add your st uff here;
    } else if (httpStatus === 400) {
      // 400 - BAD_REQUEST
      LogService.warning(this, m, 'Error 400', resp.error);
      // Add your stuff here;
    } else if (httpStatus === 401) {
      // 401 - UNAUTHORIZED
      LogService.warning(this, m, 'Error 401', resp.error);
      this.tokenService.clear();
      this.store.dispatch(AuthStoreAction.logoutExecuted());
      if (!this.router.url.startsWith('/auth/login')) {
        this.router.navigate(['/auth/login', { outlets: { configurationOutlet: null, usecasesOutlet: null } }]);
      }
      return throwError(() => resp);
    } else if (httpStatus === 404) {
      // 404 - NOT_FOUND
      LogService.warning(this, m, 'Error 404', resp.error);
      // Add your stuff here;
    } else if (httpStatus === 500) {
      // 500 - INTERNAL_SERVER_ERROR
      LogService.error(this, m, 'Error 500', resp.error);
      this.store.dispatch(ErrorHandlingStoreAction.setError());
      // Add your stuff here;
    } else if (httpStatus === 502 || httpStatus === 504) {
      // Upstream/proxy timeout/gateway errors: backend is reachable, do not enter "server unreachable" mode.
      LogService.error(this, m, `Error ${httpStatus}`, resp.error);
      this.store.dispatch(ErrorHandlingStoreAction.setError());
      // combineLatest([
      //   this.store.pipe(select(AuthStoreSelectors.selectIsAuthenticated)),
      //   this.store.pipe(select(AuthStoreSelectors.selectIsAuthenticatedTenantAdmin)),
      // ])
      //   .pipe(take(1))
      //   .subscribe(([isAuth, isTenantAuth]) => {
      //     if (isAuth && !isTenantAuth) {
      //       this.store.dispatch(NotificationsStoreAction.refreshNotificationCount());
      //     }
      //   });
    } else {
      // NOT MANAGED ERRORS;
      const msg = 'NOT MANAGED Error (' + httpStatus + ')';
      LogService.warning(this, m, msg, resp.error);
      // Add your stuff here;
    }

    const retVal = this.showErrorWithOrWithoutToastr(resp);

    return retVal;
  }

  private showErrorWithOrWithoutToastr(resp: HttpErrorResponse) {
    const m = this.showErrorWithOrWithoutToastr.name;

    const errorResponse: SystemErrorResponse = {
      ...new SystemErrorResponse(),
      ...resp.error,
    };

    // const responsePath = resp.error.path;
    // const responseMethod = resp.error.httpMethod;
    // const responseErrorKey = resp.error.errorKey;
    // const responseErrorId = resp.error.errorId;

    if (errorResponse.path && errorResponse.httpMethod && errorResponse.errorKey) {
      const fePath = errorResponse.path.substr(1); /* remove first slash */
      const excludedMethod = ExcludedAPIServices.map.get(fePath);
      if (errorResponse.httpMethod === excludedMethod) {
        const msg = 'Toastr Alert omitted because this API service belongs to list of excludeds';
        LogService.info(this, m, msg, null);
      } else {
        LogService.error(this, m, this.translate.instant(errorResponse.errorKey), null);
        this.messageNotifierService.showSystemErrorMessage(errorResponse);
      }
    } else if (resp.url && resp.url.includes('assets')) {
      const msg = 'Toastr Alert omitted because this API service belongs to assets';
      LogService.info(this, m, msg, null);
    } else if (!resp.error) {
      LogService.error(this, m, this.translate.instant(resp.message), null);
      this.messageNotifierService.showFixedErrorMessage(_('toastr_systemerror_generic_error_title'));
    } else {
      LogService.error(this, m, this.translate.instant(resp.message), null);
      this.messageNotifierService.showSystemErrorMessage(errorResponse);
    }

    return throwError(() => resp);
  }
}
